import React, { useState, useEffect } from "react";
import "./UserManagement.css";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Divider,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PersonIcon from "@mui/icons-material/Person";
import AxiosUrl from "../AxiosUrl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function UserManagement() {
  const uid = localStorage.getItem("id");
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [packages, setPackages] = useState([]);
  const [userBalance, setUserBalance] = useState(null);

  // Form states
  const [creditAmount, setCreditAmount] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [durationDays, setDurationDays] = useState(30);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await AxiosUrl.get(`/packages`, {
        headers: { token },
      });
      if (response.data && response.data.packages) {
        setPackages(response.data.packages);
      }
    } catch (error) {
      console.error("Failed to fetch packages:", error);
      toast.error("Failed to load packages");
    }
  };

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setUsers([]);
      return;
    }

    try {
      setLoading(true);
      // Use POST /users/list endpoint like UserSelector does
      const params = new URLSearchParams({ page: 1, limit: 20 });
      params.append("query", query.trim());

      const response = await AxiosUrl.post(
        `/users/list?${params.toString()}`,
        {}, // Empty body, query is in URL params
        {
          headers: { token },
        }
      );

      if (response.data && response.data.success && response.data.data) {
        setUsers(response.data.data);
      } else if (response.data && response.data.users) {
        setUsers(response.data.users);
      } else if (response.data && Array.isArray(response.data)) {
        setUsers(response.data);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      // Try fallback to GET /users/search
      try {
        const fallbackResponse = await AxiosUrl.get(`/users/search?q=${encodeURIComponent(query)}`, {
          headers: { token },
        });
        if (fallbackResponse.data && fallbackResponse.data.success && fallbackResponse.data.users) {
          setUsers(fallbackResponse.data.users);
        } else {
          setUsers([]);
        }
      } catch (fallbackError) {
        console.error("Fallback search error:", fallbackError);
        toast.error("Failed to search users");
        setUsers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event, newValue, reason) => {
    if (reason === "input") {
      // User is typing
      const inputValue = typeof newValue === "string" ? newValue : (newValue?.mobile || newValue?.username || "");
      setSearchQuery(inputValue);
      if (inputValue && inputValue.length >= 2) {
        searchUsers(inputValue);
      } else {
        setUsers([]);
      }
    } else if (reason === "clear") {
      // User cleared the input
      setSelectedUser(null);
      setSearchQuery("");
      setUsers([]);
      setUserBalance(null);
    } else if (newValue && typeof newValue === "object") {
      // User selected an option
      setSelectedUser(newValue);
      setSearchQuery(newValue.mobile || newValue.username || newValue.UniqueId || "");
      // Use balance from search results directly (already available)
      setUserBalance(newValue.Balance || 0);
      // Optionally fetch fresh balance in background (non-blocking)
      // fetchUserBalance(newValue._id);
    }
  };

  const fetchUserBalance = async (userId) => {
    // Balance is already included in search results, so we can use it directly
    // This function is kept for potential future use if we need to refresh balance
    // For now, we'll use the balance from selectedUser
    try {
      // Try to fetch fresh balance, but don't fail if it errors
      const response = await AxiosUrl.get(`/user-balance/${userId}`, {
        headers: { token },
      });
      if (response.data && response.data.Balance !== undefined) {
        setUserBalance(response.data.Balance);
      }
    } catch (error) {
      // Don't log error or show toast - just use balance from search results
      // The balance is already available in selectedUser.Balance
      console.log("Using balance from search results");
      // Set balance from selectedUser if available
      if (selectedUser && selectedUser.Balance !== undefined) {
        setUserBalance(selectedUser.Balance);
      }
    }
  };

  const handleAddCredit = async () => {
    if (!selectedUser) {
      toast.error("Please select a user first");
      return;
    }

    if (!creditAmount || parseFloat(creditAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      setLoading(true);
      const response = await AxiosUrl.post(
        `/add-credit/${uid}`,
        {
          userId: selectedUser._id,
          amount: parseFloat(creditAmount),
          reason: creditReason || "Admin credit",
        },
        {
          headers: { token },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Credit added successfully");
        setCreditAmount("");
        setCreditReason("");
        // Update balance from response if available, otherwise increment locally
        if (response.data.newBalance !== undefined) {
          setUserBalance(response.data.newBalance);
          // Also update selectedUser balance for display
          setSelectedUser({ ...selectedUser, Balance: response.data.newBalance });
        } else if (creditAmount) {
          // Increment balance locally if response doesn't include new balance
          const newBalance = (userBalance !== null ? userBalance : (selectedUser.Balance || 0)) + parseFloat(creditAmount);
          setUserBalance(newBalance);
          setSelectedUser({ ...selectedUser, Balance: newBalance });
        }
      } else {
        toast.error(response.data.message || "Failed to add credit");
      }
    } catch (error) {
      console.error("Add credit error:", error);
      toast.error(
        error.response?.data?.message || "Failed to add credit to user"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribePackage = async () => {
    if (!selectedUser) {
      toast.error("Please select a user first");
      return;
    }

    if (!selectedPackage) {
      toast.error("Please select a package");
      return;
    }

    if (!durationDays || durationDays < 1) {
      toast.error("Please enter a valid duration");
      return;
    }

    try {
      setLoading(true);
      const response = await AxiosUrl.post(
        `/subscribe-package/${uid}`,
        {
          userId: selectedUser._id,
          packageId: selectedPackage.packageId,
          durationDays: parseInt(durationDays),
        },
        {
          headers: { token },
        }
      );

      if (response.data.success) {
        toast.success(
          response.data.message || "Package subscribed successfully"
        );
        setSelectedPackage(null);
        setDurationDays(30);
        // Update balance from response if available
        if (response.data.newBalance !== undefined) {
          setUserBalance(response.data.newBalance);
          // Also update selectedUser balance for display
          setSelectedUser({ ...selectedUser, Balance: response.data.newBalance });
        }
      } else {
        toast.error(response.data.message || "Failed to subscribe package");
      }
    } catch (error) {
      console.error("Subscribe package error:", error);
      toast.error(
        error.response?.data?.message || "Failed to subscribe package"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-management-container">
      <ToastContainer position="top-center" />
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          User Account Management
        </Typography>

        {/* User Search */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Select User
            </Typography>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                const parts = [];
                if (option.mobile) parts.push(option.mobile);
                if (option.username && option.username !== "No name") parts.push(option.username);
                if (option.UniqueId) parts.push(`ID: ${option.UniqueId}`);
                return parts.length > 0 ? parts.join(" - ") : "Unknown User";
              }}
              value={selectedUser}
              inputValue={searchQuery}
              onInputChange={(event, newValue, reason) => {
                handleSearchChange(event, newValue, reason);
              }}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
                if (newValue) {
                  setSearchQuery(newValue.mobile || newValue.username || newValue.UniqueId || "");
                  // Use balance from search results directly (already available)
                  setUserBalance(newValue.Balance || 0);
                  // Optionally fetch fresh balance in background (non-blocking)
                  // fetchUserBalance(newValue._id);
                } else {
                  setSearchQuery("");
                  setUserBalance(null);
                }
              }}
              loading={loading}
              freeSolo
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search by mobile, username, or UniqueId"
                  variant="outlined"
                  fullWidth
                  placeholder="Type at least 2 characters to search..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                // Get photo URL - prioritize profilePhoto object, then individual fields
                const getPhotoUrl = () => {
                  if (option.profilePhoto && typeof option.profilePhoto === 'object') {
                    return option.profilePhoto.web || option.profilePhoto.mobile || option.profilePhoto.default || null;
                  }
                  return option.photo || option.photoWeb || option.photoMobile || null;
                };

                return (
                  <Box component="li" {...props}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                      <Avatar 
                        src={getPhotoUrl()} 
                        alt={option.username || "User"}
                        sx={{ width: 40, height: 40 }}
                      >
                        <PersonIcon />
                      </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" noWrap>
                        {option.mobile || "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {option.username || "No name"}
                        {option.UniqueId && ` | ID: ${option.UniqueId}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Balance: {option.Balance || 0} Tk
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                );
              }}
            />

            {selectedUser && (() => {
              // Get photo URL - prioritize profilePhoto object, then individual fields
              const getPhotoUrl = () => {
                if (selectedUser.profilePhoto && typeof selectedUser.profilePhoto === 'object') {
                  return selectedUser.profilePhoto.web || selectedUser.profilePhoto.mobile || selectedUser.profilePhoto.default || null;
                }
                return selectedUser.photo || selectedUser.photoWeb || selectedUser.photoMobile || null;
              };

              return (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar 
                      src={getPhotoUrl()} 
                      alt={selectedUser.username || "User"}
                      sx={{ width: 56, height: 56 }}
                    >
                      <PersonIcon />
                    </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">
                      <strong>Selected User:</strong> {selectedUser.mobile || "N/A"} -{" "}
                      {selectedUser.username || "No name"}
                      {selectedUser.UniqueId && ` (ID: ${selectedUser.UniqueId})`}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Current Balance:</strong> {userBalance !== null ? userBalance : (selectedUser.Balance || 0)} Tk
                    </Typography>
                  </Box>
                </Box>
              </Alert>
              );
            })()}
          </CardContent>
        </Card>

        {selectedUser && (
          <Grid container spacing={3}>
            {/* Add Credit Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    <AccountBalanceWalletIcon
                      sx={{ mr: 1, verticalAlign: "middle" }}
                    />
                    Add Credit to Account
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <TextField
                    label="Amount (Tk)"
                    type="number"
                    fullWidth
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">৳</InputAdornment>
                      ),
                    }}
                    helperText="Enter amount to add to user balance"
                  />

                  <TextField
                    label="Reason (Optional)"
                    fullWidth
                    multiline
                    rows={3}
                    value={creditReason}
                    onChange={(e) => setCreditReason(e.target.value)}
                    sx={{ mb: 2 }}
                    placeholder="e.g., Refund, Bonus, Support credit"
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleAddCredit}
                    disabled={loading || !creditAmount}
                    sx={{ py: 1.5 }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Add Credit"
                    )}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Subscribe Package Section */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    <LocalOfferIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Subscribe Package
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Package</InputLabel>
                    <Select
                      value={selectedPackage?.packageId || ""}
                      onChange={(e) => {
                        const pkg = packages.find(
                          (p) => p.packageId === e.target.value
                        );
                        setSelectedPackage(pkg);
                      }}
                      label="Select Package"
                    >
                      {packages.map((pkg) => (
                        <MenuItem key={pkg.packageId} value={pkg.packageId}>
                          {pkg.packageId} - {pkg.price} Tk ({pkg.contacts}{" "}
                          contacts)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedPackage && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Package:</strong> {selectedPackage.packageId}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Price:</strong> {selectedPackage.price} Tk
                      </Typography>
                      <Typography variant="body2">
                        <strong>Contacts:</strong> {selectedPackage.contacts}
                      </Typography>
                    </Alert>
                  )}

                  <TextField
                    label="Duration (Days)"
                    type="number"
                    fullWidth
                    value={durationDays}
                    onChange={(e) => setDurationDays(e.target.value)}
                    sx={{ mb: 2 }}
                    helperText="Subscription duration in days"
                    inputProps={{ min: 1 }}
                  />

                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleSubscribePackage}
                    disabled={loading || !selectedPackage}
                    sx={{ py: 1.5 }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Subscribe Package"
                    )}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {!selectedUser && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Please search and select a user to manage their account
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </div>
  );
}

export default UserManagement;
